import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";

import { ArrowIcon } from "@/assets/icons";
import { RegistrationScreen } from "@/components/RegistrationScreen/RegistrationScreen";
import Button from "@/ui/Button/Button";
import Typography from "@/ui/Typography/Typography";
import { useReferalsDataContext } from "@/store/ReferalsProvider";
import { useMultiplierDataContext } from "@/store/MultiplierProvider";
import { useUserInfoStore } from "@/store/userInfoStore";
import { MultiplierReferralsPage } from "@/types/referals";
import {
  calculateDaysLeft,
  capitalizeFirst,
  declinateOrders,
  getDaysText,
  pluralize,
  pluralizeWithCount,
  COUNTED_FRIENDS,
  DAYS,
  FRIENDS,
  FRIENDS_ACC,
  FRIENDS_GEN,
  NEW_FRIENDS,
  ORDERS_INS,
  REMAINS,
  SUCH_FRIENDS,
} from "@/utils/helpers";

import { openTelegramUrl, telegramChatLink } from "@/utils/telegram";

import { addDaysToDate, formatMultiplierDate } from "./utils";
import cls from "./styles.module.scss";
import { AdviceBlock } from "@/components/AdviceBlock/AdviceBlock";

const botNames = {
  delivery: "BogatiyKurierBot",
  taxi: "BogatiyTaxistBot",
  eats: "BK_EdaBot",
};

const serviceLabels: Record<string, string> = {
  delivery: "Доставка",
  taxi: "Такси",
  eats: "Еда",
};

const formatJoinedAt = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const UserPlusIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M15 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8" cy="7" r="4" />
    <path d="M19 8v6M22 11h-6" />
  </svg>
);

const MultiplierReferralCard = ({
  referral,
  ordersRequired,
  periodDays,
  showPeriodLabel,
}: {
  referral: MultiplierReferralsPage["results"][number];
  ordersRequired: number;
  periodDays: number;
  showPeriodLabel: boolean;
}) => {
  const progress = ordersRequired
    ? Math.min(100, (referral.orders_done / ordersRequired) * 100)
    : 0;
  // Выполнившему цель показываем ровно «N из N», а не фактический перевыполненный
  // счётчик — сверх цели заказы на бонус уже не влияют.
  const displayedOrders = ordersRequired
    ? Math.min(referral.orders_done, ordersRequired)
    : referral.orders_done;

  const telegramLink = telegramChatLink(referral.telegram_user);

  const openTelegram = () => {
    if (!telegramLink) return;
    openTelegramUrl(telegramLink);
  };

  return (
    <article className={cls.referralCard}>
      <div className={cls.referralTop}>
        {telegramLink ? (
          <button
            type="button"
            data-trigger="ref.multiplier.referral_username"
            className={cls.referralNameLink}
            onClick={openTelegram}
          >
            <Typography component="strong">{referral.name}</Typography>
            <span className={cls.referralNameArrow} aria-hidden="true">
              →
            </span>
          </button>
        ) : (
          <Typography component="strong">{referral.name}</Typography>
        )}
        <span>
          {serviceLabels[referral.service_type] || referral.service_type}
        </span>
      </div>
      <Typography>В парке с {formatJoinedAt(referral.joined_at)}</Typography>
      <div className={cls.referralProgressHeading}>
        <div className={referral.qualified ? cls.qualified : undefined}>
          <strong>{displayedOrders}</strong>
          <span>из {ordersRequired}</span>
        </div>
        {showPeriodLabel && (
          <span>за последние {pluralizeWithCount(periodDays, DAYS)}</span>
        )}
      </div>
      <div className={cls.referralProgress}>
        <span
          className={referral.qualified ? cls.qualifiedProgress : undefined}
          style={{ width: `${progress}%` }}
        />
      </div>
    </article>
  );
};

export const ReferralMultiplierPage = () => {
  const navigate = useNavigate();
  const [isRegistrationScreenOpen, setIsRegistrationScreenOpen] = useState(
    false
  );
  const yandexId = useUserInfoStore((state) => state.yandexId);
  const serviceType = useUserInfoStore((state) => state.serviceType);
  const { inviteText } = useReferalsDataContext();
  const {
    multiplierInfo,
    multiplierReferrals,
    multiplierReferralsLoading,
    loadMoreMultiplierReferrals,
  } = useMultiplierDataContext();
  const normalizedServiceType =
    serviceType === "delivery" || serviceType === "taxi" ? serviceType : "eats";
  const completedReferrals = multiplierInfo?.target?.referrals_done ?? 0;
  const targetReferrals = multiplierInfo?.target?.referrals_required ?? 0;
  const periodDays = multiplierInfo?.period_days ?? 30;
  const ordersPerReferral =
    multiplierInfo?.target?.orders_per_referral ??
    multiplierReferrals?.orders_required ??
    0;
  const targetMultiplier =
    multiplierInfo?.target?.multiplier ??
    multiplierInfo?.current.multiplier ??
    1;
  const activeMultiplier = multiplierInfo?.current.multiplier;
  const isMultiplierActive =
    activeMultiplier !== null && activeMultiplier !== undefined;
  const displayMultiplier = activeMultiplier ?? targetMultiplier;
  const expiresAt = multiplierInfo?.current.expires_at ?? null;
  const formattedExpiry = formatMultiplierDate(expiresAt);
  // Слева всегда базовая сумма без множителя, справа — цель. Иначе при
  // активном множителе обе половины показывают одно и то же значение и
  // сравнение теряет смысл.
  const baseBonus = multiplierInfo?.rewards.base ?? 0;
  const targetBonus = multiplierInfo?.rewards.target ?? 0;
  const currentBonus = multiplierInfo?.rewards.current ?? 0;
  const isCollecting = !isMultiplierActive && completedReferrals > 0;
  const kicker = isMultiplierActive
    ? "МНОЖИТЕЛЬ АКТИВЕН"
    : isCollecting
    ? "ОСТАЛОСЬ НЕМНОГО"
    : "РЕФЕРАЛЬНАЯ ПРОГРАММА";
  const remainingReferrals = Math.max(targetReferrals - completedReferrals, 0);
  // Конец следующего периода — если продлить прямо сейчас
  const nextPeriodEnd = formatMultiplierDate(
    addDaysToDate(expiresAt, periodDays)
  );

  // Дольше всех в парке — выше; выполнившие цель уходят вниз списка
  const sortedReferrals = useMemo(() => {
    const results = multiplierReferrals?.results ?? [];

    return [...results].sort((a, b) => {
      if (a.qualified !== b.qualified) return a.qualified ? 1 : -1;
      return new Date(a.joined_at).getTime() - new Date(b.joined_at).getTime();
    });
  }, [multiplierReferrals]);

  // Выполнивший цель реферал «выходит из счёта», когда истекает его окно
  // (joined_at + период). Предупреждаем про того, у кого осталось меньше
  // 10 дней; если таких несколько — про самого срочного.
  const expiringReferral = useMemo(() => {
    const candidates = (multiplierReferrals?.results ?? [])
      .filter((referral) => referral.qualified)
      .map((referral) => ({
        referral,
        daysLeft: calculateDaysLeft(
          addDaysToDate(referral.joined_at, periodDays)
        ),
      }))
      .filter(({ daysLeft }) => daysLeft > 0 && daysLeft < 10);

    if (!candidates.length) return null;

    return candidates.reduce((closest, candidate) =>
      candidate.daysLeft < closest.daysLeft ? candidate : closest
    );
  }, [multiplierReferrals, periodDays]);
  const steps = isMultiplierActive
    ? [
        `Сейчас каждый приглашённый друг приносит тебе ${
          displayMultiplier === 2
            ? "двойной бонус"
            : `бонус ×${displayMultiplier}`
        } — ${currentBonus.toLocaleString("ru-RU")} ₽`,
        `Друг засчитывается, когда выполнит ${declinateOrders(
          ordersPerReferral
        )}`,
        `Собери ${targetReferrals} ${pluralize(
          targetReferrals,
          COUNTED_FRIENDS
        )}${
          formattedExpiry ? ` до ${formattedExpiry}` : ""
        } — и ×${displayMultiplier} продлится ещё на ${pluralizeWithCount(
          periodDays,
          DAYS
        )}`,
        `Не соберёшь — множитель выключится, но ${targetReferrals} ${pluralize(
          targetReferrals,
          NEW_FRIENDS
        )} за последние ${pluralizeWithCount(
          periodDays,
          DAYS
        )} снова откроют его`,
      ]
    : [
        "Приглашай друзей по своей ссылке или QR-коду",
        `Каждый друг выполняет ${declinateOrders(ordersPerReferral)}`,
        `Собери ${targetReferrals} ${pluralize(
          targetReferrals,
          SUCH_FRIENDS
        )} за ${pluralizeWithCount(periodDays, DAYS)}`,
        `Получишь бонус х${targetMultiplier} за всех последующих друзей на ${pluralizeWithCount(
          periodDays,
          DAYS
        )}`,
      ];

  const inviteFriend = () => {
    if (!yandexId) {
      setIsRegistrationScreenOpen(true);
      return;
    }

    const inviteLink = `https://t.me/${botNames[normalizedServiceType]}?start=yr_${yandexId}_RMMA`;
    const shareText = inviteText?.main[normalizedServiceType] || "";
    const encodedText = encodeURIComponent(`${shareText} \n\n ${inviteLink}`);

    window.open(`https://t.me/share/url?url=${encodedText}`, "_blank");
  };

  return (
    <>
      <RegistrationScreen isOpen={isRegistrationScreenOpen} />

      <main className={cls.page}>
        <header className={cls.header}>
          <Button
            trigger="ref.multiplier.back"
            variant="iconOnly"
            className={cls.backButton}
            onClick={() => navigate("/")}
            ariaLabel="Назад"
            icon={<ArrowIcon />}
          />
          <Typography component="h1" variant="h4">
            Увеличенный бонус
          </Typography>
        </header>

        <section
          className={classNames(cls.hero, isMultiplierActive && cls.activeHero)}
        >
          <div className={cls.heroTop}>
            <div className={cls.heroHeading}>
              <span className={cls.kicker}>{kicker}</span>
              <Typography component="h2" variant="h1">
                {isMultiplierActive ? (
                  <>
                    Твой бонус{" "}
                    {displayMultiplier === 2
                      ? "удвоен"
                      : `увеличен ×${displayMultiplier}`}
                    {formattedExpiry && (
                      <span className={cls.heroTitleGold}>
                        {" "}
                        до {formattedExpiry}
                      </span>
                    )}
                  </>
                ) : isCollecting ? (
                  `${capitalizeFirst(
                    pluralize(remainingReferrals, REMAINS)
                  )} ${pluralizeWithCount(
                    remainingReferrals,
                    FRIENDS
                  )} до ×${targetMultiplier}`
                ) : targetMultiplier === 2 ? (
                  "Удвой доход с приглашённых друзей"
                ) : (
                  "Увеличь доход с приглашённых друзей"
                )}
              </Typography>
              {isMultiplierActive && (
                <Typography className={cls.heroLead}>
                  Каждый друг сейчас приносит{" "}
                  {currentBonus.toLocaleString("ru-RU")} ₽
                </Typography>
              )}
            </div>
            <span className={cls.heroMultiplier}>×{displayMultiplier}</span>
          </div>

          <div className={cls.bonusComparison}>
            <div className={cls.regularBonus}>
              <span>Обычный бонус</span>
              <strong>{baseBonus.toLocaleString("ru-RU")} ₽</strong>
            </div>
            <span className={cls.bonusArrow} aria-hidden="true">
              →
            </span>
            <div className={cls.multipliedBonus}>
              <span>С множителем ×{targetMultiplier}</span>
              <strong>{targetBonus.toLocaleString("ru-RU")} ₽</strong>
            </div>
          </div>

          {!isMultiplierActive && multiplierInfo?.target && (
            <>
              <div className={cls.progressHeading}>
                <div>
                  <strong>{completedReferrals}</strong>
                  <span>из {targetReferrals}</span>
                </div>
                <span>за последние {pluralizeWithCount(periodDays, DAYS)}</span>
              </div>

              <div
                className={cls.progress}
                aria-label={`Выполнено ${completedReferrals} из ${targetReferrals}`}
              >
                {Array.from({ length: targetReferrals }).map((_, index) => (
                  <span
                    key={index}
                    className={
                      index < completedReferrals
                        ? cls.progressComplete
                        : undefined
                    }
                  />
                ))}
              </div>

              <Typography className={cls.hint}>
                Пригласи{" "}
                <span>{pluralizeWithCount(targetReferrals, FRIENDS_ACC)}</span>{" "}
                с {pluralizeWithCount(ordersPerReferral, ORDERS_INS)} за{" "}
                {getDaysText(periodDays)} — и получай множитель ×
                {targetMultiplier} к своему бонусу
              </Typography>
            </>
          )}

          {isMultiplierActive && (
            <div className={cls.activeNote}>
              <Typography>
                Действует на всех новых друзей — без ограничений
              </Typography>
              <Typography component="strong">
                Например: приглашая по 2 друга в день, за месяц можешь получить
                до{" "}
                <span>
                  {(2 * periodDays * currentBonus).toLocaleString("ru-RU")} ₽
                </span>
              </Typography>
            </div>
          )}

          <Button
            trigger="ref.multiplier.invite_friend"
            className={cls.inviteButton}
            onClick={inviteFriend}
            label="Пригласить друга"
          />
        </section>

        {isMultiplierActive && multiplierInfo?.target && (
          <section className={cls.renewalCard}>
            <div className={cls.renewalHead}>
              <Typography component="h2">
                Продли ×{targetMultiplier} ещё на{" "}
                {pluralizeWithCount(periodDays, DAYS)}
              </Typography>
              <span className={cls.renewalChip}>след. период</span>
            </div>

            <div className={cls.renewalProgressRow}>
              <div>
                <strong>{completedReferrals}</strong>
                <span>
                  из {targetReferrals} {pluralize(targetReferrals, FRIENDS_GEN)}
                </span>
              </div>
              <span>выполнили {declinateOrders(ordersPerReferral)}</span>
            </div>

            <div
              className={cls.progress}
              aria-label={`Выполнено ${completedReferrals} из ${targetReferrals}`}
            >
              {Array.from({ length: targetReferrals }).map((_, index) => (
                <span
                  key={index}
                  className={
                    index < completedReferrals
                      ? cls.progressComplete
                      : undefined
                  }
                />
              ))}
            </div>

            <Typography className={cls.renewalHint}>
              Друзья, приглашённые сейчас, идут в зачёт следующего периода:
              собери {targetReferrals} — и ×{displayMultiplier} продолжит
              действовать
              {formattedExpiry && nextPeriodEnd
                ? ` с ${formattedExpiry} по ${nextPeriodEnd}`
                : " без перерыва"}
              .
            </Typography>
          </section>
        )}

        {!isMultiplierActive &&
          completedReferrals > 0 &&
          multiplierInfo?.period_ends_at && (
            <section className={cls.periodNotice}>
              <span aria-hidden="true">◷</span>
              <Typography>
                Текущий период завершится{" "}
                {formatMultiplierDate(multiplierInfo.period_ends_at)}. Успей
                выполнить условие для следующего множителя.
              </Typography>
            </section>
          )}

        <AdviceBlock canClose={false} />

        {multiplierInfo?.target && (
          <section className={cls.stepsCard}>
            <Typography component="h2" variant="body4">
              Как это работает
            </Typography>
            <ol>
              {steps.map((step, index) => (
                <li key={step}>
                  <span>{index + 1}</span>
                  <Typography>{step}</Typography>
                </li>
              ))}
            </ol>
          </section>
        )}

        <section className={cls.referralsSection}>
          <Typography component="h2" variant="h4">
            Твои рефералы
          </Typography>

          {!isMultiplierActive && expiringReferral && multiplierInfo?.target && (
            <div className={cls.referralWarning}>
              <span aria-hidden="true">◷</span>
              <Typography>
                {expiringReferral.referral.name} выйдет из счёта через{" "}
                {pluralizeWithCount(expiringReferral.daysLeft, DAYS)} — станет{" "}
                {Math.max(completedReferrals - 1, 0)} из {targetReferrals}
              </Typography>
            </div>
          )}

          {multiplierReferrals && sortedReferrals.length ? (
            <div className={cls.referralsList}>
              {sortedReferrals.map((referral) => (
                <MultiplierReferralCard
                  key={`${referral.name}-${referral.joined_at}`}
                  referral={referral}
                  ordersRequired={multiplierReferrals.orders_required}
                  periodDays={periodDays}
                  showPeriodLabel={!isMultiplierActive}
                />
              ))}

              {multiplierReferrals.results.length <
                multiplierReferrals.count && (
                <Button
                  trigger="ref.multiplier.load_more"
                  variant="white"
                  className={cls.loadMoreButton}
                  onClick={loadMoreMultiplierReferrals}
                  disabled={multiplierReferralsLoading}
                  loading={multiplierReferralsLoading}
                  label="Показать ещё"
                />
              )}
            </div>
          ) : (
            <div className={cls.emptyReferrals}>
              <div className={cls.emptyIcon}>
                <UserPlusIcon />
              </div>
              <Typography component="strong">Пока никого нет</Typography>
              <Typography>
                {isMultiplierActive
                  ? `Приглашённые друзья появятся здесь. Пригласи первого — и начни путь к продлению множителя ×${displayMultiplier} на следующий период`
                  : `Приглашённые друзья появятся здесь. Пригласи первого — и начни путь к х${targetMultiplier}`}
              </Typography>
              <Button
                trigger="ref.multiplier.invite_friend"
                variant="text"
                className={cls.emptyInviteButton}
                onClick={inviteFriend}
                label={
                  <>
                    Пригласить друга <span aria-hidden="true">→</span>
                  </>
                }
              />
            </div>
          )}
        </section>
      </main>
    </>
  );
};
